import {
  ColorType,
  CrosshairMode,
  customSeriesDefaultOptions,
} from 'lightweight-charts'

import { RoundedCandleSeriesRenderer } from './rounded-candles-series-renderer'

import type { RoundedCandleSeriesOptions } from './rounded-candles-series-renderer'
import type {
  CustomSeriesPricePlotValues,
  ICustomSeriesPaneView,
  PaneRendererCustomData,
  WhitespaceData,
  Time,
  DeepPartial,
  ChartOptions,
  CandlestickData,
} from 'lightweight-charts'

export class RoundedCandleSeries<TData extends CandlestickData>
  implements ICustomSeriesPaneView<Time, TData, RoundedCandleSeriesOptions>
{
  _renderer: RoundedCandleSeriesRenderer<TData>

  constructor() {
    this._renderer = new RoundedCandleSeriesRenderer()
  }

  static chartOptions(): DeepPartial<ChartOptions> {
    return {
      grid: {
        horzLines: {
          visible: false,
        },
        vertLines: {
          visible: false,
        },
      },
      autoSize: true,
      layout: {
        background: {
          type: ColorType.Solid,
          color: 'transparent',
        },
        attributionLogo: false,
      },
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: false },
      timeScale: {
        visible: false,
        fixLeftEdge: true,
        // smaller spacing means more bars can be shown in the same width
        maxBarSpacing: 6,
      },
      crosshair: {
        mode: CrosshairMode.Hidden,
      },
      handleScroll: false,
      handleScale: false,
    }
  }

  priceValueBuilder(plotRow: TData): CustomSeriesPricePlotValues {
    return [plotRow.high, plotRow.low, plotRow.close]
  }

  renderer(): RoundedCandleSeriesRenderer<TData> {
    return this._renderer
  }

  isWhitespace(data: TData | WhitespaceData): data is WhitespaceData {
    return (data as Partial<TData>).close === undefined
  }

  update(
    data: PaneRendererCustomData<Time, TData>,
    options: RoundedCandleSeriesOptions,
  ): void {
    this._renderer.update(data, options)
  }

  defaultOptions = () => {
    return {
      ...customSeriesDefaultOptions,
      priceLineVisible: false,
      upColor: 'green',
      downColor: 'red',
      // intentionally left as is
      // see: https://github.com/tradingview/lightweight-charts/blob/master/plugin-examples/src/plugins/rounded-candles-series/rounded-candles-series.ts#L35
      radius: () => 0,
    }
  }
}
